import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Fab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { ResponseOfOrganizationDetails } from "services/KnowfullyAdminClient";
import { OrganizationType } from "types/common-types";
import OrganizationAccessLevels from "./OrganizationAccessLevels";
import OrganizationAdministrators from "./OrganizationAdminUsers";
import OrganizationApplicationDomains from "./OrganizationApplicationDomains";
import OrganizationCatalogSkus from "./OrganizationCatalogSkus";
import OrganizationDynamicMenus from "./OrganizationDynamicMenus";
import OrganizationChildren from "./OrganizationChildren";
import OrganizationContracts from "./OrganizationContracts";
import OrganizationDefaultProgressionGates from "./OrganizationDefaultProgressionGates";
import OrganizationExternalReferences from "./OrganizationExternalReferences";
import OrganizationPartners from "./OrganizationPartners";
import OrganizationUpdateForm from "./OrganizationUpdateForm";

const OrganizationDetails = () => {
  const { formatMessage: t } = useIntl();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const { organizationsUrl, organizationService } = useGlobalContext();

  const { organizationId } = useParams<{
    organizationId: string;
  }>();

  const organizationResult = useStrictQuery<ResponseOfOrganizationDetails>(
    ["organization", organizationId],
    () => organizationService.getOrganization(organizationId!),
    {
      enabled: !!organizationId,
    }
  );

  const organization = organizationResult?.data?.data;
  useTitle(organization?.name || "Organization Details");

  const rows = [
    RowElement("id", organization?.id ?? "", organizationResult.isLoading),
    RowElement(
      "organizationNameLabel",
      organization?.name ?? "",
      organizationResult.isLoading
    ),
    RowElement(
      "parentOrganizationLabel",
      organization?.parentName ?? "",
      organizationResult.isLoading
    ),
    RowElement(
      "descriptionLabel",
      organization?.description ?? "",
      organizationResult.isLoading
    ),
    RowElement(
      "displayNameLabel",
      organization?.displayName ?? "",
      organizationResult.isLoading,
      <span
        dangerouslySetInnerHTML={{ __html: organization?.displayName ?? "" }}
      ></span>
    ),
    RowElement(
      "accountIdLabel",
      organization?.accountId ?? "",
      organizationResult.isLoading
    ),
    RowElement(
      "organizationTypeLabel",
      organization?.organizationTypeName ?? "",
      organizationResult.isLoading
    ),
    RowElement(
      "organizationStatusLabel",
      organization?.organizationStatusName ?? "",
      organizationResult.isLoading
    ),
  ].filter((r) => !!r.component);

  if (organizationResult.isLoading) {
    return <Loader />;
  }

  if (updateOpen) {
    return (
      <OrganizationUpdateForm
        organization={organization!}
        onOrganizationUpdated={updateOpenActions.setFalse}
        onCancel={updateOpenActions.setFalse}
      />
    );
  }

  return (
    <>
      {!organizationResult.isFetching && (
        <>
          <RedirectBackButton
            redirectRoute={organizationsUrl()}
            labelKey="backToListButtonLabel"
          />
          <h2>
            <b> {t({ id: "organization" })}</b>
          </h2>

          <Box my={3} display="flex" justifyContent="center">
            <Fab color="primary" onClick={updateOpenActions.setTrue}>
              <EditIcon />
            </Fab>
          </Box>
          <Box mb={4}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  {rows.map(({ title, component }, i) => (
                    <TableRow key={title}>
                      <TableCell component="th" scope="row">
                        {title}
                      </TableCell>
                      <TableCell>{component}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <h2>
            <T id="propertiesLabel" />
          </h2>

          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Accordion key="organizationChildren">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="childOrganizations" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationChildren organization={organization!} />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item>
              <Accordion key="defaultProgressionGates">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="defaultProgressionGates" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationDefaultProgressionGates
                    organization={organization!}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item>
              <Accordion key="organizationAdministrators">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="administrators" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationAdministrators organization={organization!} />
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item>
              <Accordion key="organizationDynamicMenus">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="dynamicMenus" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationDynamicMenus organization={organization!} />
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item>
              <Accordion key="organizationCatalogSkus">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="catalogSkus" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationCatalogSkus organization={organization!} />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item>
              <Accordion key="accessLevels">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="accessLevels" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationAccessLevels organization={organization!} />
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item>
              <Accordion key="partners">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="partners" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationPartners organization={organization!} />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item>
              <Accordion key="externalReferences">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="externalReferences" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationExternalReferences
                    organization={organization!}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item>
              <Accordion key="applicationDomains">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <T id="applicationDomains" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrganizationApplicationDomains
                    organization={organization!}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            {organization?.organizationTypeId === OrganizationType.Client && (
              <Grid item>
                <Accordion key="contracts">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      <T id="contracts" />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <OrganizationContracts organization={organization} />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
};

export default OrganizationDetails;
